/**
 * Weekly business hours of a Central team (doc 25 section 7; owner Q-367).
 *
 * The hours are LOCAL WALL-CLOCK windows: "Monday 08:00 to 18:00" means 08:00 on the clock of each market the team serves,
 * resolved through that market's IANA timezone at the moment of asking (a country can span timezones, and the clocks change
 * on their own). Nothing here is stored in UTC and nothing names a country or a zone: the zone comes from the market.
 *
 * A window never crosses midnight (write two windows: the evening of one day and the morning of the next); `end` may be
 * `24:00`. Windows of one day must not overlap. Pure and deterministic.
 */
/** ISO weekday order: Monday first. */
export const WEEKDAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const CLOCK = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
/** Minutes since local midnight of `HH:MM` (`24:00` is 1440 and only valid as an end), or undefined when it is not a clock time. */
export function clockMinutes(text, allowMidnightEnd = false) {
    if (allowMidnightEnd && text === '24:00')
        return 1440;
    if (!CLOCK.test(text))
        return undefined;
    return Number(text.slice(0, 2)) * 60 + Number(text.slice(3, 5));
}
export const isWeekday = (value) => typeof value === 'string' && WEEKDAYS.includes(value);
/**
 * Validate and normalize the hours of a team (sorted by weekday then start; exact duplicates removed). A team that is
 * `alwaysOpen` carries no windows (24 h, every day); any other team needs at least one.
 */
export function normalizeWeeklyHours(windows, alwaysOpen) {
    if (alwaysOpen)
        return windows.length === 0 ? { ok: true, hours: [] } : { ok: false, code: 'HOURS_NOT_ALLOWED_ALWAYS_OPEN' };
    if (windows.length === 0)
        return { ok: false, code: 'HOURS_REQUIRED' };
    const parsed = [];
    for (const raw of windows) {
        if (typeof raw !== 'object' || raw === null)
            return { ok: false, code: 'HOURS_WINDOW_INVALID' };
        const w = raw;
        if (!isWeekday(w.day) || typeof w.start !== 'string' || typeof w.end !== 'string')
            return { ok: false, code: 'HOURS_WINDOW_INVALID' };
        const from = clockMinutes(w.start);
        const to = clockMinutes(w.end, true);
        if (from === undefined || to === undefined)
            return { ok: false, code: 'HOURS_WINDOW_INVALID' };
        if (to <= from)
            return { ok: false, code: 'HOURS_WINDOW_ORDER' };
        parsed.push({ window: { day: w.day, start: w.start, end: w.end }, from, to });
    }
    parsed.sort((a, b) => WEEKDAYS.indexOf(a.window.day) - WEEKDAYS.indexOf(b.window.day) || a.from - b.from || a.to - b.to);
    const out = [];
    for (const p of parsed) {
        const last = out[out.length - 1];
        if (last !== undefined && last.window.day === p.window.day) {
            if (last.from === p.from && last.to === p.to)
                continue;
            if (p.from < last.to)
                return { ok: false, code: 'HOURS_WINDOWS_OVERLAP' };
        }
        out.push(p);
    }
    return { ok: true, hours: out.map((p) => p.window) };
}
/** The local weekday and minute of day of a UTC instant on a market's clock. Undefined when the zone or the instant is not valid. */
export function localClock(instantUtc, timeZone) {
    const at = Date.parse(instantUtc);
    if (Number.isNaN(at))
        return undefined;
    try {
        const parts = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date(at));
        const weekday = parts.find((p) => p.type === 'weekday')?.value.slice(0, 3).toUpperCase();
        const hour = Number(parts.find((p) => p.type === 'hour')?.value);
        const minute = Number(parts.find((p) => p.type === 'minute')?.value);
        if (weekday === undefined || !isWeekday(weekday) || !Number.isInteger(hour) || !Number.isInteger(minute))
            return undefined;
        return { day: weekday, minute: (hour % 24) * 60 + minute };
    }
    catch {
        return undefined;
    }
}
/**
 * True when the hours are open at that instant ON THE CLOCK of `timeZone` (the market's IANA zone). An `alwaysOpen` team is open
 * always; an unreadable zone or instant is never "open" (fail closed: the caller decides what closed means, e.g. the assistant keeps
 * resolving and states the reply time).
 */
export function isOpenAt(hours, instantUtc, timeZone) {
    if (hours.alwaysOpen)
        return true;
    const clock = localClock(instantUtc, timeZone);
    if (clock === undefined)
        return false;
    return hours.weeklyHours.some((w) => {
        if (w.day !== clock.day)
            return false;
        const from = clockMinutes(w.start);
        const to = clockMinutes(w.end, true);
        return from !== undefined && to !== undefined && clock.minute >= from && clock.minute < to;
    });
}
//# sourceMappingURL=hours.js.map